'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '@/components/ui/page-header';
import { TableCard } from '@/components/ui/table-card';
import { useTablePage } from '@/hooks/use-table-page';
import { useAuditEventsList, type AuditEvent, type AuditEventOptions } from '@/lib/api/audit';

import { AuditEventDetailSheet } from './components/AuditEventDetailSheet';
import { AuditTrailTable } from './components/AuditTrailTable';
import {
  auditTrailFilterMapping,
  auditTrailSortMapping,
  auditTrailQueryParamsMapping,
} from './utils/table-mappings';

const EMPTY_OPTIONS: AuditEventOptions = {
  action: [],
  entityType: [],
  operation: [],
  actor: [],
  storeId: [],
};

export default function AuditTrailPage() {
  const { t } = useTranslation('settings');

  const { setTableState, queryInput } = useTablePage({
    initialSorting: [{ id: 'createdAt', desc: true }],
    filterMapping: auditTrailFilterMapping,
    sortMapping: auditTrailSortMapping,
    queryParamsMapping: auditTrailQueryParamsMapping,
  });

  const {
    data: listResponse,
    isLoading,
    isFetching,
    refetch,
  } = useAuditEventsList(queryInput);

  const auditEvents = listResponse?.auditEvents ?? [];
  const total = listResponse?.total ?? 0;
  const options = listResponse?.options ?? EMPTY_OPTIONS;

  const [selectedEvent, setSelectedEvent] = React.useState<AuditEvent | null>(null);

  return (
    <div className="flex flex-col space-y-4 sm:space-y-6">
      <PageHeader title={t('auditTrail.title')} description={t('auditTrail.description')} />

      <TableCard>
        <AuditTrailTable
          auditEvents={auditEvents}
          total={total}
          options={options}
          isLoading={isLoading || isFetching}
          onTableStateChange={setTableState}
          onRowClick={setSelectedEvent}
          onRefetch={refetch}
        />
      </TableCard>

      <AuditEventDetailSheet
        event={selectedEvent}
        open={selectedEvent !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedEvent(null);
        }}
      />
    </div>
  );
}
